import { useCallback, useEffect, useState } from "react";
import useMinerPortsRepository from "./MinerPorts.repository";
import { MODALS_ENUM } from "@constants/Modals";
import WithModal from "@components/HOC/withModals";
import { dev } from "@utils/dev";
import "./MinerPorts.modal.scss";
import Icon from "@components/global/new/Icon/Icon";
import delete_icon from "@assets/redesign/icons/trash.svg";
import Loader from "@components/global/Loader/Loader";
import useMinerPorts from "@services/miner/MinerPorts.hook";
import { IReproxyPortDesc } from "@services/getMinerList";
import { useIntl } from "react-intl";
import ReproxyLink from "@components/global/ReproxyLink/ReproxyLink";
import { ReproxyPortListEventTag } from "../minerReducer";
import { link } from "fs";

interface IPortLink {
  port: number;
  target: string;
  minerId: number;
  description: string;
  lastActive: string;
  // [key: string]: { // динамические ключи (например, "10001", "10002")
  //   Target: string;
  //   LastActive: string; // можно заменить на тип Date, если будет парситься
  // };
}

function MinerPortsModal() {

  const repo = useMinerPortsRepository();
  const { formatMessage } = useIntl();

  const [isAlive, setAlive] = useState(true);
  const [isTableLoading, setTableLoading] = useState(true);
  const [items, setItems] = useState<IPortLink[]>([]);

  const onPortChanged = useCallback((
    port: number,
    prevPort: number,
    minerId: number
  ) => {
    setItems(prevItems => {
      //const filtered = prevItems.filter(item => item.port !== prevPort || item.minerId === minerId);
      return prevItems
        .filter(item => item.port !== port || item.minerId === minerId)
        .map(item => {
          if (item.port === prevPort && item.minerId === minerId) {
            return { ...item, port };
          }
          return item;
        });
    });
  }, [setItems]);

  const {
    deleteMinerPort,
    createPortAndOpen
  } = useMinerPorts({
    onPortChanged
  });

  const onDeleteConfirmed = async (
    minerId: number,
    port: number
  ) => {
    await deleteMinerPort(minerId, port);
    setItems(prev => prev.filter(x => x.port !== port));
  }

  useEffect(() => {
    (async () => {
      setTableLoading(true);
      const response = await repo.list.request();
      if (response && response.Status === "success" && response.Record) {
        const links: IPortLink[] = [];
        (response.Record.Links ?? []).forEach(p => {
          let descObj: IReproxyPortDesc = {
            Id: 0,
            Desc: "",
          };
          try {
            const parsed = JSON.parse(p.TargetDesc) as IReproxyPortDesc;
            descObj = parsed;
          } catch { }
          links.push({
            port: p.Port,
            target: p.Target,
            minerId: descObj.Id,
            description: descObj.Desc,
            lastActive: p.LastActive,
          });
        });
        document.dispatchEvent(
          new CustomEvent(ReproxyPortListEventTag, {
            detail: {
              ports: links.map(l => ({ minerId: l.minerId, port: l.port }))
            },
          })
        );
        setItems(links);
        setAlive(response.Record.ReproxyConnected > 0);
      }
      setTableLoading(false);
    })();
  }, []);

  return (
    <div className="minerPorts__container">
      {!isTableLoading && (
        <span className={
          isAlive
            ? "minerPorts__status minerPorts__status--active"
            : "minerPorts__status minerPorts__status--disabled"
        }>
          {isAlive
            ? <>{formatMessage({ id: "miners.ports.system_online" })}</>
            : <>{formatMessage({ id: "miners.ports.system_offline" })}</>
          }
        </span>
      )}
      <br /><br />
      <div className="grid-table">
        <div className="grid-row header">
          <div className="grid-cell">
            {formatMessage({ id: "miners.ports.list.port" })}
          </div>
          <div className="grid-cell">
            {formatMessage({ id: "miners.ports.list.description" })}
          </div>
          <div className="grid-cell">
            {formatMessage({ id: "miners.ports.list.target" })}
          </div>
          <div className="grid-cell">
            {formatMessage({ id: "miners.ports.list.last_active" })}
          </div>
          <div className="grid-cell"></div>
        </div>
        {isTableLoading && (
          <Loader />
        )}
        {items.length === 0 && !isTableLoading && (
          <div className="grid-row full-width">
            <div className="grid-cell">
              {formatMessage({ id: "common.no_items" })}
            </div>
          </div>
        )}
        {items.map((item, i) => (
          <div className="grid-row" key={i}>
            <div className="grid-cell grid-cell__port">
              {item.port}
              <ReproxyLink
                minerId={item.minerId}
                link={{
                  Port: item.port,
                  Target: item.target,
                  LastActive: item.lastActive,
                }}
                onDoPortClicked={createPortAndOpen}
              />
            </div>
            <div className="grid-cell">{item.description}</div>
            <div className="grid-cell">{item.target}</div>
            <div className="grid-cell">{item.lastActive}</div>
            <div className="grid-cell">
              <Icon
                src={delete_icon}
                onClick={() => {
                  onDeleteConfirmed(item.minerId, item.port);
                }}
                title={formatMessage({ id: "miners.ports.delete_port" })}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default WithModal(MODALS_ENUM.MINERS_PORTS, MinerPortsModal);